import * as yup from 'yup'

import { type FulfillmentFormValues, type MarketplaceForm, type ServiceAreaForm } from './types'

const currentYear = new Date().getFullYear()
const integerPattern = /^\d+$/
const rublePattern = /^\d+(?:[.,]\d{1,2})?$/
const yandexPattern = /^https:\/\/(?:www\.)?yandex\.ru\/maps\/org\/[^\s/?#]+(?:[/?#].*)?$/i

/**
 * Карточка организации в 2ГИС. Город в адресе есть не всегда, региональные поддомены вида
 * `spb.2gis.ru` встречаются наравне с основным доменом, а номер организации — только цифры.
 * Перед `firm` допускаем любое число сегментов: адрес, скопированный из строки браузера после
 * поиска, выглядит как `.../moscow/search/фулфилмент/firm/70000001019374429/37.6,55.7`, и
 * отвергать его нельзя — именно так карточку и находят. Домен верхнего уровня любой: у 2ГИС
 * кроме `2gis.ru` есть `2gis.kz`, `2gis.ae` и другие страны, а карточки там такие же.
 */
const twoGisPattern = /^https:\/\/(?:[\w-]+\.)*2gis\.[a-z]{2,}\/(?:[^\s?#]+\/)?firm\/\d+(?:[/?#].*)?$/i

const isHttpUrl = (value: string) => {
  try {
    const url = new URL(value)
    const hostParts = url.hostname.split('.')
    return (url.protocol === 'http:' || url.protocol === 'https:') && hostParts.length >= 2 && hostParts.every(Boolean)
  } catch {
    return false
  }
}

const optionalInteger = (label: string) =>
  yup
    .string()
    .test('integer', `${label}: укажите целое неотрицательное число`, (value) => !value || integerPattern.test(value))

const optionalRubles = yup
  .string()
  .test('rubles', 'Используйте не более двух знаков после запятой', (value) => !value || rublePattern.test(value))

/**
 * Прайс проверяем мягко: обязательных полей тут нет, пустая цена означает «по запросу». Состав
 * услуг приходит из справочника сервера, поэтому перечислить его в схеме заранее невозможно.
 */
const serviceDraftSchema = yup.object({
  serviceId: yup.string().required(),
  isOffered: yup.boolean().required(),
  ladder: yup.array(yup.object({ volumeFrom: yup.number().required(), price: optionalRubles })).required(),
})

const servicePricesSchema = yup.object({
  fbs: yup.array(serviceDraftSchema),
  fbo: yup.array(serviceDraftSchema),
  dbs: yup.array(serviceDraftSchema),
  other: yup.array(serviceDraftSchema),
})

const citySchema = yup.object({
  name: yup.string().trim().max(160).required(),
  regionName: yup.string().trim().max(160).required(),
})

const serviceAreaSchema = yup.object({
  mode: yup.mixed<'all' | 'selected'>().oneOf(['all', 'selected']).required(),
  cities: yup.array().of(citySchema).required(),
})

const locationSchema = yup.object({
  city: citySchema.nullable().required('Выберите город склада'),
  address: yup.string().trim().max(250, 'Не более 250 символов'),
})

const marketplaceSchema = yup.object({
  marketplace: yup.mixed<MarketplaceForm['marketplace']>().required(),
  otherName: yup
    .string()
    .max(160)
    .when('marketplace', {
      is: 'other',
      then: (value) => value.trim().required('Укажите название другого маркетплейса'),
    }),
  coverageMode: yup.mixed<'all' | 'selected'>().oneOf(['all', 'selected']).required(),
  destinations: yup.array().when('coverageMode', {
    is: 'selected',
    then: (value) => value.min(1, 'Выберите хотя бы один федеральный округ'),
  }),
})

const selectedCitiesRequired = (area: ServiceAreaForm | undefined, shipmentTypes: string[] | undefined, type: string) =>
  !shipmentTypes?.includes(type) || area?.mode === 'all' || Boolean(area?.cities.length)

export const fulfillmentFormSchema = yup.object({
  name: yup.string().trim().min(2, 'Укажите название').max(160, 'Не более 160 символов').required('Укажите название'),
  inn: yup
    .string()
    .matches(/^(?:\d{10}|\d{12})$/, 'ИНН должен содержать 10 или 12 цифр')
    .required('Укажите ИНН'),
  description: yup
    .string()
    .trim()
    .min(30, 'Описание должно содержать не менее 30 символов')
    .max(5000, 'Описание должно содержать не более 5000 символов')
    .required('Укажите описание'),
  shipmentTypes: yup
    .array()
    .of(yup.mixed<FulfillmentFormValues['shipmentTypes'][number]>().required())
    .min(1, 'Выберите хотя бы один тип поставки')
    .required(),
  locations: yup
    .array()
    .of(locationSchema)
    .min(1, 'Укажите город, где находится ваш склад')
    .max(20, 'Можно указать не более 20 складов')
    .test('unique-city', 'Город склада указан дважды', (items) => {
      const cityKeys = (items ?? []).flatMap((item) =>
        item.city ? [`${item.city.name.trim().toLowerCase()}|${item.city.regionName.trim().toLowerCase()}`] : []
      )
      return new Set(cityKeys).size === cityKeys.length
    })
    .required(),
  fbsArea: serviceAreaSchema.test('fbs-cities', 'Добавьте хотя бы один город для FBS', function (area) {
    return selectedCitiesRequired(area, this.parent.shipmentTypes, 'fbs')
  }),
  dbsArea: serviceAreaSchema.test('dbs-cities', 'Добавьте хотя бы один город для DBS', function (area) {
    return selectedCitiesRequired(area, this.parent.shipmentTypes, 'dbs')
  }),
  marketplaces: yup
    .array()
    .of(marketplaceSchema)
    .test('fbs-marketplaces', 'Выберите хотя бы один маркетплейс', function (items) {
      return !this.parent.shipmentTypes?.includes('fbs') || Boolean(items?.length)
    })
    .required(),
  categoryMode: yup.mixed<'all' | 'excluding'>().oneOf(['all', 'excluding']).required(),
  deliveryFrequency: yup
    .string()
    .test('delivery-frequency', 'Укажите, как часто вы возите товар на маркетплейс', function (value) {
      // При FBS и DBS заказ уезжает покупателю сразу, поэтому скорость доставки обязательна.
      const types: string[] = this.parent.shipmentTypes ?? []
      return !(types.includes('fbs') || types.includes('dbs')) || Boolean(value)
    }),
  categoryExclusions: yup
    .string()
    .max(2000)
    .when('categoryMode', {
      is: 'excluding',
      then: (value) => value.trim().required('Укажите исключения категорий'),
    }),
  otherShipmentDescription: yup
    .string()
    .max(1000)
    .when('shipmentTypes', {
      is: (types: string[]) => types?.includes('other'),
      then: (value) => value.trim().required('Опишите другой способ отгрузки'),
    }),
  areaSquareMeters: optionalInteger('Площадь'),
  operationStartedYear: yup.string().test('year', `Год должен быть от 1990 до ${currentYear}`, (value) => {
    if (!value) {
      return true
    }
    const year = Number(value)
    return integerPattern.test(value) && year >= 1990 && year <= currentYear
  }),
  activeClientCount: optionalInteger('Активные клиенты'),
  minimumUnitPriceRubles: optionalRubles,
  servicePrices: servicePricesSchema,
  yandexMapsUrl: yup
    .string()
    .test('yandex', 'Нужна ссылка вида https://yandex.ru/maps/org/...', (value) => !value || yandexPattern.test(value)),
  twoGisUrl: yup
    .string()
    .test(
      'two-gis',
      'Нужна ссылка вида https://2gis.ru/moscow/firm/70000001089422217',
      (value) => !value || twoGisPattern.test(value.trim())
    ),
  websiteUrl: yup
    .string()
    .test('website', 'Укажите полный адрес сайта с http:// или https://', (value) => !value || isHttpUrl(value)),
  phone: yup.string().max(80),
  email: yup.string().email('Проверьте адрес электронной почты'),
  telegram: yup.string().max(255),
  max: yup.string().max(255),
})
